import { ReactNode, useCallback, useEffect, useState } from 'react'
import { InputGroup, Popover } from '@systemeio/components'
import { SearchMdIcon } from '@systemeio/icons'
import { ParseKeys } from 'i18next'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import { useDebouncedValue } from 'shared/hooks/use-debounced-value'
import { useCommunitySearchSuggestions } from 'modules/community/api/get-community-search-suggestions'
import { buildSearchUrl } from './utils'

interface FormData {
  search: string
}

interface Props {
  className?: string
  placeholder?: string
}

export function CommunitySearchForm({ className, placeholder }: Props): ReactNode {
  const router = useRouter()
  const { asPath, query } = router
  const { communityPath, topicPath, q } = query
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(-1)

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: { search: '' },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const [debouncedSearch] = useDebouncedValue(watch('search'), 300)
  const { data: suggestions } = useCommunitySearchSuggestions({
    path: communityPath,
    topicPath: topicPath,
    query: isValid ? debouncedSearch : undefined,
  })
  const hasSuggestions = !!suggestions?.length

  const error = errors.search?.message as ParseKeys
  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen])
  const formRef = useClickOutside<HTMLFormElement>(handleClickOutside)

  useEffect(() => {
    if (asPath.includes('/search') && q) {
      setValue('search', q as string, { shouldValidate: true, shouldDirty: true })
    }
  }, [asPath, q, setValue])

  useEffect(() => {
    setActiveIndex(-1)
  }, [suggestions, isOpen, debouncedSearch])

  const onSubmit = ({ search }: FormData): void => {
    router.push(buildSearchUrl(search, communityPath, topicPath))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!suggestions?.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setIsOpen(true)
      setActiveIndex(prev => (prev + 1) % suggestions.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setIsOpen(true)
      setActiveIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1))
      return
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault()
      const selected = suggestions[activeIndex]
      setValue('search', selected)
      router.push(buildSearchUrl(selected, communityPath, topicPath))
      setIsOpen(false)
    }
  }

  return (
    <form
      ref={formRef}
      onSubmit={e => {
        void handleSubmit(onSubmit)(e)
      }}
      data-testid="community-search-form"
      className={twMerge('flex flex-col gap-3 rounded-md bg-bg-primary p-5 lg:px-0', className)}
    >
      <div className="relative lg:px-5" ref={setReferenceElement}>
        <InputGroup.Root size="md" className="w-full">
          <InputGroup.Input
            type="text"
            data-testid="search-input"
            className="hide-webkit-search-cancel-button"
            placeholder={placeholder ?? t('global.search')}
            onFocus={() => {
              setIsOpen(true)
            }}
            onKeyDown={handleKeyDown}
            {...register('search', {
              validate: value => {
                const trimmed = value.trim()
                if (!trimmed) return false
                if (trimmed.length < 3) return 'community.search.errors.min_length'
                return true
              },
            })}
          />
          <InputGroup.Addon align="inline-end" appearance="subtle">
            <button
              type="submit"
              disabled={!isValid}
              data-testid="search-submit-button"
              aria-label={t('global.search')}
              className="cursor-pointer text-text-secondary transition-colors hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              <SearchMdIcon aria-hidden="true" className="size-4" />
            </button>
          </InputGroup.Addon>
        </InputGroup.Root>
      </div>
      <p className="text-center text-sm text-text-error">{t(error ?? '')}</p>
      <Popover.Root open={isOpen && hasSuggestions} onOpenChange={setIsOpen}>
        <Popover.Portal>
          <Popover.Positioner
            anchor={referenceElement}
            side="bottom"
            sideOffset={15}
            className="z-30"
          >
            <Popover.Popup
              className="min-w-60 rounded-lg bg-bg-primary py-5 shadow-2"
              style={{ width: 'var(--anchor-width)' }}
            >
              {/* Navigate on mousedown: the form's useClickOutside and the popover's
                  outside-press dismissal both fire before the click event and would
                  unmount the suggestion before a click could land. */}
              <ul
                className="flex flex-col gap-2"
                onMouseDown={event => event.stopPropagation()}
                onTouchStart={event => event.stopPropagation()}
              >
                {suggestions?.map((suggestion, index) => (
                  <li key={suggestion} className="w-full">
                    <Link
                      title={suggestion}
                      href={buildSearchUrl(suggestion, communityPath, topicPath)}
                      className={twMerge(
                        'inline-block w-full cursor-pointer truncate pl-5 text-text-primary hover:bg-bg-page',
                        activeIndex === index && 'bg-bg-page',
                      )}
                      onMouseDown={event => {
                        event.preventDefault()
                        event.stopPropagation()
                        setIsOpen(false)
                        router.push(buildSearchUrl(suggestion, communityPath, topicPath))
                      }}
                      onClick={() => setIsOpen(false)}
                    >
                      {suggestion}
                    </Link>
                  </li>
                ))}
              </ul>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </form>
  )
}
